import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import { VtxProgress, VtxTooltip } from '@vertexinc/vtx-ui-react-component-library';
import S3UploaderProps, { S3UploaderStatus } from './S3UploaderProps.interface';
import './upload-s3.scss';
import { uploadS3WithPresignedUrl, s3UploadResponse, s3UploadFile } from './FileUploadS3';
import {
  httpStatusToText,
  Logging,
  Notification,
  NotificationData,
  ResponseError,
} from '../..';

// Max Number if times a S3 file Upload will be retried
const maxNumberRetries = 5;

const S3Uploader = ({
  gatewayEndPoint,
  uuid,
  files,
  fileNameUrlMap,
  removeUpload,
  statusUpload = null,
  linkReference = null,
  progressWidth = 40,
}: S3UploaderProps): JSX.Element => {
  const [progressStatus, setProgressStatus] = React.useState(null);
  const [pendingFiles, setPendingFiles] = React.useState(files);
  const [bytesUploaded, setBytesUploaded] = React.useState(0); // Completed Files
  const [bytesUploading, setBytesUploading] = React.useState(0); // Current File
  const dispatch = useDispatch();
  const [notification, setNotification] = React.useState(null);
  const [retry, setRetry] = React.useState(0);
  const [startTime] = React.useState(performance.now());
  const [fileCount, setFileCount] = React.useState(1);

  // Get the Total Byte Size that needs to be Uploaded
  const getTotalBytes = (): number => {
    let bytes = 0;

    files?.forEach((file: File) => {
      bytes += file.size;
    });

    return bytes;
  };

  const totalBytes = getTotalBytes();

  const onNotificationClose = (): void => {
    setNotification(null);

    // Upload is complete. Remove it
    removeUpload(uuid);
  };

  const setException = (reason: string, reasonCode: number): void => {
    setProgressStatus('exception');

    Logging.Info('Set S3 Exception', reason, reasonCode);

    const reasonDescription = reasonCode ? `${reasonCode} ${httpStatusToText(reasonCode)} - ${reason}` : reason;

    // If required, notify about the Update in Status
    if (statusUpload) {
      statusUpload(uuid, S3UploaderStatus.FAILED, reasonDescription);
    }

    const notificationData: NotificationData = {
      type: 'Error',
      title: 'S3 Upload Transfer Error',
      description: reasonDescription,
      autoClose: false,
      hideProgress: true,
    };

    setNotification(notificationData);
  };

  // Remove the File from the Array and return the modified array
  const removeFileFromArray = (file: File, filesArray: File[]): File[] => {
    const index: number = filesArray.indexOf(file);

    if (index !== -1) {
      filesArray.splice(index, 1);
    }
    return filesArray;
  };

  // Calculate the Estimated Remaining Upload Time
  const calculateTransferTimeRemaining = (): string => {
    const currentTime = performance.now();
    const speedBytesPerSecond = Math.round(currentTime > startTime ? (bytesUploading + bytesUploaded) / ((currentTime - startTime) / 1000) : 0);
    const secondsTotal = speedBytesPerSecond ? totalBytes / speedBytesPerSecond : 0;
    const secondsRemaining = Math.round(speedBytesPerSecond ? (totalBytes - (bytesUploading + bytesUploaded)) / speedBytesPerSecond : 0);

    let estimated = null;

    if (secondsRemaining < 60) {
      const seconds = secondsRemaining;

      estimated = `${seconds} second${seconds === 1 ? '' : 's'}`;
    } else if (secondsRemaining < (60 * 60)) {
      const minutes = Math.floor(secondsRemaining / 60);
      const seconds = Math.round(secondsRemaining - (minutes * 60));

      estimated = `${minutes} minute${minutes === 1 ? '' : 's'} ${seconds} second${seconds === 1 ? '' : 's'}`;
    } else {
      const hours = Math.floor(secondsRemaining / 360);
      const minutes = Math.floor((secondsRemaining - (hours * 360)) / 60);
      const seconds = Math.round(secondsRemaining - (hours * 360) - (minutes * 60));

      estimated = `${hours} hour${hours === 1 ? '' : 's'} ${minutes} minute${minutes === 1 ? '' : 's'} ${seconds} second${seconds === 1 ? '' : 's'}`;
    }

    return `Estimated Remaining: ${estimated}`;
  };

  const setUploadResponse = (uploadResponse: s3UploadResponse): void => {
    if (pendingFiles) {
      // Success
      if (uploadResponse.status === 200) {
        Logging.Info('S3 Upload Success:', fileCount, uploadResponse.s3File.name);

        // Update the Bytes already Uploaded
        setBytesUploaded(bytesUploaded + uploadResponse.s3File.size);

        // Reset the Bytes uploading
        setBytesUploading(0);

        // Reset the Retry Count
        setRetry(0);

        // Increment the File Count
        setFileCount(fileCount + 1);

        // Remove the File from the Pending Files
        let localPendingFiles: File[] = _.cloneDeep(pendingFiles);
        localPendingFiles = removeFileFromArray(uploadResponse.file, localPendingFiles);
        setPendingFiles(localPendingFiles);
      } else if (uploadResponse.status === 0) {
        // Ignore
        Logging.Info('S3 Upload Response:', fileCount, uploadResponse);
      } else { // Failure
        Logging.Info('S3 Upload Failure:', fileCount, uploadResponse);

        // Retry the Upload
        if (retry < maxNumberRetries) { // eslint-disable-line  no-lonely-if
          Logging.Info('Retrying S3 Upload:', retry + 1, uploadResponse.s3File.name);

          // Reset the Bytes uploaded during the Failure
          setBytesUploading(0);

          // Increment the Retry Count
          setRetry(retry + 1);
        } else {
          Logging.Info('Aborting S3 Upload');

          setException(uploadResponse.responseText, uploadResponse.status);

          // Abort the Uploading of all the Files
          setPendingFiles(null);
        }
      }
    } else {
      Logging.Info('Ignoring Response');
    }
  };

  const setUploadProgress = (s3File: s3UploadFile, uploadProgress: number, fileBytesUploaded: number, fileBytesToUpload: number): void => {
    if (pendingFiles) {
      Logging.Debug('S3 Upload Progress:', s3File.name, uploadProgress, fileBytesUploaded, fileBytesToUpload);

      setBytesUploading(fileBytesUploaded);
    } else {
      Logging.Info('Ignoring Progress');
    }
  };

  const uploadFile = (file: File, data: string | ArrayBuffer): void => {
    // const data = await getBlob(file.url);
    const s3File: s3UploadFile = {
      name: file.name,
      type: file.type,
      size: file.size,
      data,
    };

    const url = fileNameUrlMap.get(file.name);

    uploadS3WithPresignedUrl({
      file,
      url,
      s3File,
      setUploadProgress,
      setUploadResponse,
    });
  };

  // Load the File Asynchronously.  Update the Status on Error or Success
  const loadFile = (file: File): void => {
    const reader = new FileReader();

    // Reader Abort
    reader.onabort = (): void => {
      setException(`Loading file '${file.name}' was Aborted.`, undefined);
    };
    // Reader Error
    reader.onerror = (): void => {
      setException(`Error Loading file '${file.name}'.`, undefined);
    };

    // Reader Success
    reader.onload = (): void => {
      const binaryStr = reader.result;

      Logging.Info('File Loaded:', file.name);

      uploadFile(file, binaryStr);
    };

    reader.readAsArrayBuffer(file);
  };

  React.useEffect(() => {
    Logging.Info('Pending S3 Files:', pendingFiles, retry);

    if (progressStatus !== 'exception') {
      // Check if files are Pending to be Loaded
      if (pendingFiles && pendingFiles.length > 0) {
        // Get the First File to be loaded
        const file: File = pendingFiles[0];
        const url = fileNameUrlMap.get(file.name);

        Logging.Info('Processing Pending S3 File:', file, url);

        if (!url) {
          setException(`Invalid Url for file '${file.name}'.`, undefined);
        } else {
          // Load the File
          loadFile(file);
        }
      } else if (statusUpload) {
        // If required, notify about the Update in Status
        statusUpload(uuid, S3UploaderStatus.SUCCESS, undefined);
      }
    }
  }, [pendingFiles, retry]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Info('UploadS3 - Initial Render');

    return (): void => {
      // This Code will run once the component unmounts.
      Logging.Info('UploadS3 - Unmounts');
      setPendingFiles(null);
      setNotification(null);
    };
  }, []);

  // Calculate the Progress
  const progress = Math.round(((bytesUploading + bytesUploaded) / totalBytes) * 100);
  const estimatedRemaining = calculateTransferTimeRemaining();

  Logging.Debug('Progress', progress, bytesUploading, bytesUploaded, totalBytes);

  return (
    <>
      <a href={linkReference} className="headerProgress">
        <VtxTooltip title={estimatedRemaining}>
          <VtxProgress percent={progress} type="circle" size="small" width={progressWidth} status={progressStatus} />
        </VtxTooltip>
      </a>
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default S3Uploader;
